import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';
import type AskAllTrailsQueryType from '@alltrails/amplitude/enums/AskAllTrailsQueryType';
import type CardLocation from '@alltrails/amplitude/enums/CardLocation';
import type { AskAllTrailsArgs, AskLocation } from '@/utils/askAllTrailsApi';

/**
 * One visit to Ask, from opening it to closing it, which its analytics events share. Moving between pages keeps it; a full
 * page load starts over.
 */
export type AskSession = {
  id: string;
  /** Where Ask was opened from. */
  entryPoint: CardLocation;
};

/**
 * Ask AllTrails mode on /explore. While it's active the sidebar shows Ask, the map shows only Ask's pins, and the hosting
 * page's own searches and pin updates are paused without touching Explore's state.
 *
 * Deliberately left out of `useResetState`, so Back from a page can return to Ask.
 */
export type AskState = {
  isActive: boolean;
  /**
   * Whether a page that hosts Ask is mounted. Ask stays active as the user moves between pages, but only a host page shows
   * it; a page becomes a host by calling `useAskMode`.
   */
  isAvailable: boolean;
  /**
   * The questions asked, oldest first, each as the request it sends. Built once, at submit, with the viewport copied in, and
   * never rebuilt from live state: panning the map shouldn't change the request. Outside a chat there is only ever one; a new
   * question replaces it.
   *
   * Cleared when Ask closes, so reopening it starts fresh. Kept while Ask stays on but the user is on a page that doesn't
   * host it (ex: a trail page), so Back shows the same answers from the cache.
   */
  turns: AskAllTrailsArgs[];
  /**
   * The turn the user is reading, whose answer the map shows. A new question takes it; in a chat, scrolling the transcript
   * moves it.
   */
  activeRequestId: string | undefined;
  /** How the latest question was asked. */
  queryType: AskAllTrailsQueryType | undefined;
  /** The server's chat the turns are asked in, once its first question has created it. */
  chatId: string | undefined;
  /** Kept after Ask closes, so a late response still logs the session it was asked in. */
  session: AskSession | undefined;
};

export const initialState: AskState = {
  isActive: false,
  isAvailable: false,
  turns: [],
  activeRequestId: undefined,
  queryType: undefined,
  chatId: undefined,
  session: undefined
};

export const askSlice = createSlice({
  name: 'ask',
  initialState,
  reducers: {
    askQuestion: {
      reducer: (state, action: PayloadAction<AskAllTrailsArgs, string, { queryType: AskAllTrailsQueryType }>) => {
        state.turns = action.payload.isChat ? [...state.turns, action.payload] : [action.payload];
        state.activeRequestId = action.payload.requestId;
        state.queryType = action.meta.queryType;
      },
      prepare: ({
        isChat,
        location,
        question,
        queryType
      }: {
        isChat: boolean;
        location?: AskLocation;
        question: string;
        queryType: AskAllTrailsQueryType;
      }) => ({
        payload: { isChat, requestId: nanoid(), request: { query: question, ...(location && { location }) } },
        meta: { queryType }
      })
    },
    clearAskQuestion: state => {
      state.turns = [];
      state.activeRequestId = undefined;
      state.queryType = undefined;
      state.chatId = undefined;
    },
    /**
     * The chat a question created. Ignored once that question is gone (ex: Ask closed while it loaded), so a late response
     * can't attach a chat to a conversation it wasn't asked in.
     */
    setAskChatId: (state, action: PayloadAction<{ chatId: string | undefined; requestId: string }>) => {
      if (state.turns.some(turn => turn.requestId === action.payload.requestId)) {
        state.chatId = action.payload.chatId;
      }
    },
    enterAsk: {
      reducer: (state, action: PayloadAction<AskSession>) => {
        state.isActive = true;
        state.session = action.payload;
      },
      prepare: (entryPoint: CardLocation) => ({ payload: { id: nanoid(), entryPoint } })
    },
    exitAsk: state => {
      state.isActive = false;
      state.turns = [];
      state.activeRequestId = undefined;
      state.queryType = undefined;
      state.chatId = undefined;
    },
    setActiveAskTurn: (state, action: PayloadAction<string>) => {
      if (state.turns.some(turn => turn.requestId === action.payload)) {
        state.activeRequestId = action.payload;
      }
    },
    setIsAskAvailable: (state, action: PayloadAction<boolean>) => {
      state.isAvailable = action.payload;
    }
  }
});

export const { askQuestion, clearAskQuestion, enterAsk, exitAsk, setActiveAskTurn, setAskChatId, setIsAskAvailable } = askSlice.actions;

/** The latest question: the one the map shows and the input waits on. */
export const selectLatestAskArgs = (state: { ask: AskState }) => state.ask.turns.at(-1);

/** The active turn and every turn before it, oldest first: what the map can show while the user reads that turn. */
export const selectAskTurnsThroughActive = (state: { ask: AskState }) => {
  const { turns, activeRequestId } = state.ask;
  const activeIndex = turns.findIndex(turn => turn.requestId === activeRequestId);

  return activeIndex === -1 ? turns : turns.slice(0, activeIndex + 1);
};

export default askSlice.reducer;
